// EP-161 §2.3 / S2: one visitor car on ที่จอดรถวันนี้. A table row pair on a
// desktop (SessionRow), a card on a phone (SessionCard); both share the state
// badges and the action buttons below, and open SessionDetail when expanded.
import { useEffect, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge, Button, Card } from '@/components/ui'
import { useDateTimeFormatter } from '@/hooks/useDateFormatter'
import { formatBaht } from '@/lib/money'
import type { ParkingSession, ParkingSessionState } from '@/types/parking'
import { SessionDetail } from './SessionDetail'

const STATE_VARIANT: Record<ParkingSessionState, 'info' | 'warning' | 'primary' | 'error' | 'success'> = {
  parked: 'info',
  overstay: 'warning',
  awaiting_payment: 'warning',
  slip_to_verify: 'primary',
  rejected: 'error',
  paid: 'success',
}

const VERIFIED_HOW = ['slip_checked', 'cash', 'transfer', 'bank_match', 'ml'] as const

export interface SessionActionHandlers {
  /** Owner, admin and staff; false hides every decision button. */
  canAct: boolean
  onViewSlip: (slipImageKey: string) => void
  onVerify: (session: ParkingSession) => void
  onReject: (session: ParkingSession) => void
  onMarkFraud: (session: ParkingSession) => void
}

export interface SessionRowProps extends SessionActionHandlers {
  session: ParkingSession
  expanded: boolean
  onToggle: () => void
  /** Scroll the row into view once (a push deep link). */
  scrollTo?: boolean
  /** Slot for the gate buttons (check-out, cash) of the operator app, T161-W02a. */
  gateActions?: ReactNode
}

export function SessionBadges({ session }: { session: ParkingSession }) {
  const { t } = useTranslation('parking')
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <Badge variant={STATE_VARIANT[session.state]} size="sm">
        {t(`sessions.states.${session.state}`)}
      </Badge>
      {session.fraud_flagged && (
        <Badge variant="error" size="sm">
          {t('sessions.fraud_badge')}
        </Badge>
      )}
      {session.plate_flagged && (
        <Badge variant="error" outline size="sm">
          {t('sessions.plate_flagged')}
        </Badge>
      )}
    </span>
  )
}

function useVerifiedByText(session: ParkingSession): string | null {
  const { t } = useTranslation('parking')
  const how = session.payment?.verified_how
  if (session.state !== 'paid' || !how) return null
  const known = VERIFIED_HOW.find((h) => h === how)
  if (!known) return session.payment?.verified_by_name ?? null
  const name = session.payment?.verified_by_name
  return name ? `${t(`sessions.verified_by.${known}`)} · ${name}` : t(`sessions.verified_by.${known}`)
}

export function SessionActions({
  session,
  canAct,
  onViewSlip,
  onVerify,
  onReject,
  onMarkFraud,
  gateActions,
}: SessionActionHandlers & { session: ParkingSession; gateActions?: ReactNode }) {
  const { t } = useTranslation('parking')
  const slipKey = session.payment?.slip_image_key
  const hasPayment = !!session.payment
  const canVerify = canAct && hasPayment && (session.state === 'slip_to_verify' || session.state === 'rejected')
  const canReject = canAct && hasPayment && session.state === 'slip_to_verify'
  const canFlag =
    canAct &&
    !session.fraud_flagged &&
    (session.state === 'awaiting_payment' || session.state === 'slip_to_verify' || session.state === 'rejected')

  return (
    <div className="flex flex-wrap gap-2">
      {gateActions}
      {slipKey && (
        <Button size="sm" variant="outline" onClick={() => onViewSlip(slipKey)}>
          {t('sessions.view_slip')}
        </Button>
      )}
      {canVerify && (
        <Button size="sm" variant="success" onClick={() => onVerify(session)}>
          {t('sessions.verify')}
        </Button>
      )}
      {canReject && (
        <Button size="sm" variant="error" outline onClick={() => onReject(session)}>
          {t('sessions.reject')}
        </Button>
      )}
      {canFlag && (
        <Button size="sm" variant="error" onClick={() => onMarkFraud(session)}>
          {t('sessions.fraud_mark')}
        </Button>
      )}
    </div>
  )
}

function useScrollTo<T extends HTMLElement>(enabled: boolean | undefined) {
  const ref = useRef<T>(null)
  useEffect(() => {
    if (enabled) ref.current?.scrollIntoView?.({ block: 'center' })
  }, [enabled])
  return ref
}

function amountText(amount: number | null): string {
  return amount == null ? '-' : formatBaht(amount)
}

export function SessionRow({ session, expanded, onToggle, scrollTo, gateActions, ...handlers }: SessionRowProps) {
  const { t } = useTranslation('parking')
  const formatDateTime = useDateTimeFormatter()
  const verifiedBy = useVerifiedByText(session)
  const ref = useScrollTo<HTMLTableRowElement>(scrollTo)
  const Chevron = expanded ? ChevronDown : ChevronRight

  return (
    <tbody>
      <tr ref={ref} data-testid={`parking-session-${session.id}`} className={expanded ? 'bg-base-200/40' : undefined}>
        <td>
          <button
            type="button"
            className="inline-flex items-center gap-1 font-mono font-semibold"
            aria-expanded={expanded}
            aria-label={`${session.license_plate}: ${expanded ? t('sessions.hide_detail') : t('sessions.show_detail')}`}
            onClick={onToggle}
          >
            <Chevron className="h-4 w-4" aria-hidden="true" />
            {session.license_plate}
          </button>
        </td>
        <td>{session.zone_name ?? '-'}</td>
        <td className="whitespace-nowrap">{formatDateTime(session.entered_at)}</td>
        <td className="whitespace-nowrap">{session.exited_at ? formatDateTime(session.exited_at) : '-'}</td>
        <td className="whitespace-nowrap">{amountText(session.charge_amount)}</td>
        <td>
          <SessionBadges session={session} />
          {verifiedBy && <div className="text-xs text-base-content/60 mt-1">{verifiedBy}</div>}
        </td>
        <td>
          <SessionActions session={session} gateActions={gateActions} {...handlers} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7}>
            <SessionDetail sessionId={session.id} />
          </td>
        </tr>
      )}
    </tbody>
  )
}

export function SessionCard({ session, expanded, onToggle, scrollTo, gateActions, ...handlers }: SessionRowProps) {
  const { t } = useTranslation('parking')
  const formatDateTime = useDateTimeFormatter()
  const verifiedBy = useVerifiedByText(session)
  const ref = useScrollTo<HTMLDivElement>(scrollTo)

  return (
    <div ref={ref} data-testid={`parking-session-${session.id}`}>
      <Card compact>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-mono font-semibold">{session.license_plate}</div>
            <div className="text-sm text-base-content/60">{session.zone_name ?? '-'}</div>
          </div>
          <SessionBadges session={session} />
        </div>
        <div className="text-sm space-y-0.5">
          <div>
            {t('sessions.entered')} {formatDateTime(session.entered_at)}
            {session.exited_at && ` · ${t('sessions.exited')} ${formatDateTime(session.exited_at)}`}
          </div>
          <div>{amountText(session.charge_amount)}</div>
          {verifiedBy && <div className="text-xs text-base-content/60">{verifiedBy}</div>}
        </div>
        <SessionActions session={session} gateActions={gateActions} {...handlers} />
        <Button size="sm" variant="ghost" aria-expanded={expanded} onClick={onToggle}>
          {expanded ? t('sessions.hide_detail') : t('sessions.show_detail')}
        </Button>
        {expanded && <SessionDetail sessionId={session.id} />}
      </Card>
    </div>
  )
}
